/**
 * Cart line kind + identity helpers (custom/delivery Supabase identity spec,
 * docs/superpowers/specs/2026-06-09-custom-line-item-supabase-identity-design.md).
 *
 * The cart's two virtual line items historically overload `id` with a shared
 * sentinel string — 'customProduct' / 'delivery' — so the id signals the KIND of
 * line but loses per-line IDENTITY. These helpers separate the two jobs for the
 * Supabase payload only:
 *  - kind     → `lineType: 'custom' | 'delivery'` (source of truth; absent = real product)
 *  - identity → the unique `cartItemId` Firestore already mints per line
 * Firestore keeps the sentinel ids untouched (legacy reads work forever — the
 * sentinel checks below are a permanent fallback, no backfill).
 *
 * Pure module: no imports, safe for both sales.ts and firebase.ts.
 */

export const CUSTOM_PRODUCT_SENTINEL = 'customProduct'
export const DELIVERY_SENTINEL = 'delivery'

export type PseudoLineType = 'custom' | 'delivery'

interface CartLineLike {
  id?: unknown
  lineType?: unknown
  cartItemId?: unknown
}

export function isCustomLine(item: unknown): boolean {
  if (!item || typeof item !== 'object') return false
  const i = item as CartLineLike
  return i.lineType === 'custom' || i.id === CUSTOM_PRODUCT_SENTINEL
}

export function isDeliveryLine(item: unknown): boolean {
  if (!item || typeof item !== 'object') return false
  const i = item as CartLineLike
  return i.lineType === 'delivery' || i.id === DELIVERY_SENTINEL
}

export function isPseudoLine(item: unknown): boolean {
  return isCustomLine(item) || isDeliveryLine(item)
}

/**
 * Tag an API-bound cart item with its kind + unique identity. No-op (returns the
 * same reference) for real products. Never mutates the input — the in-memory cart
 * and Firestore writes must keep the legacy sentinel shape.
 */
export function tagPseudoLineForApi<T>(item: T): T {
  if (!item || typeof item !== 'object') return item
  const kind: PseudoLineType | null = isCustomLine(item)
    ? 'custom'
    : isDeliveryLine(item)
      ? 'delivery'
      : null
  if (!kind) return item
  const i = item as CartLineLike
  const uniqueId =
    typeof i.cartItemId === 'string' && i.cartItemId ? i.cartItemId : i.id
  // `as T` is intentionally loose: callers currently pass `any`-shaped cart items, and
  // the structural addition of `lineType` isn't on the legacy CartItem type yet. Once
  // the typed cart item includes `lineType`, this cast becomes sound.
  return { ...(item as object), id: uniqueId, lineType: kind } as T
}
